import React from "react";
import "./index.css";

const Portfolio = () => {
  const sectionStyle = {
    padding: "132px 24px",
    maxWidth: "1360px",
    margin: "0 auto",
  };

  const buttonPrimary = {
    background: "#2563eb",
    color: "#ffffff",
    padding: "14px 22px",
    borderRadius: "12px",
    textDecoration: "none",
    fontWeight: "700",
    display: "inline-block",
    border: "none",
  };

  const buttonSecondary = {
    border: "1px solid #60a5fa",
    color: "#93c5fd",
    padding: "14px 22px",
    borderRadius: "12px",
    textDecoration: "none",
    fontWeight: "700",
    display: "inline-block",
    background: "transparent",
  };

  const chipStyle = {
    display: "inline-block",
    padding: "10px 14px",
    margin: "6px 8px 0 0",
    background: "rgba(30, 41, 59, 0.95)",
    borderRadius: "999px",
    border: "1px solid rgba(148, 163, 184, 0.18)",
    fontSize: "14px",
    color: "#e5e7eb",
  };

  const linkStyle = {
    color: "#93c5fd",
    textDecoration: "none",
    fontWeight: "600",
  };

  const projectImageStyle = {
    width: "100%",
    borderRadius: "16px",
    border: "1px solid rgba(96, 165, 250, 0.12)",
    marginBottom: "18px",
    objectFit: "cover",
    maxHeight: "280px",
  };

  const flagshipImageStyle = {
    width: "100%",
    borderRadius: "18px",
    border: "1px solid rgba(96, 165, 250, 0.16)",
    objectFit: "cover",
    maxHeight: "360px",
  };

  const sectionEyebrow = (text) => (
    <p
      style={{
        color: "#60a5fa",
        fontWeight: "700",
        letterSpacing: "1.4px",
        margin: "0 0 10px",
        fontSize: "13px",
        textTransform: "uppercase",
      }}
    >
      {text}
    </p>
  );

  return (
    <div className="portfolio-shell">
      <nav className="portfolio-nav">
        <div className="portfolio-nav-inner">
          <div className="portfolio-brand">Camron Njock</div>

          <div className="portfolio-nav-links">
            <a href="#about">About</a>
            <a href="#proof-of-value">Value</a>
            <a href="#what-i-build">What I Build</a>
            <a href="#flagship-project">Flagship</a>
            <a href="#selected-projects">Projects</a>
            <a href="#case-studies">Case Studies</a>
            <a href="#certifications">Certifications</a>
            <a href="#experience">Experience</a>
            <a href="#services">Services</a>
            <a href="#contact">Contact</a>
          </div>
        </div>
      </nav>

      <section className="hero-section">
        <div className="hero-inner">
          <div style={{ textAlign: "center" }}>
            <img
              src="/assets/profile-picture.jpeg"
              alt="Camron Njock portrait"
              className="hero-image"
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
            />
          </div>

          <div>
            <p className="hero-kicker">Cloud | Data | Systems Thinking</p>

            <h1 className="hero-title">AWS Cloud & Data Engineer</h1>

            <p className="hero-subtitle">
              Building scalable infrastructure, analytics systems, and production-grade cloud architectures.
            </p>

            <p className="hero-description">
              I am a Data Science student at the National College of Ireland focused on AWS architecture,
              microservices, analytics workflows, and technical case-study driven project work. My portfolio is built to
              show not just tools, but system design, deployment thinking, and real engineering direction.
            </p>

            <div className="hero-actions">
              <a href="#flagship-project" style={buttonPrimary}>View Flagship Project</a>
              <a href="/assets/Camron-Njock-CV.pdf" download style={buttonSecondary}>Download CV</a>
              <a
                href="https://www.linkedin.com/in/camronnjock/"
                target="_blank"
                rel="noreferrer"
                style={buttonSecondary}
              >
                LinkedIn
              </a>
              <a
                href="https://github.com/cam-spec"
                target="_blank"
                rel="noreferrer"
                style={buttonSecondary}
              >
                GitHub
              </a>
            </div>

            <div className="hero-meta">
              Dublin • Email:{" "}
              <a href="mailto:camronnjock@gmail.com" style={linkStyle}>
                camronnjock@gmail.com
              </a>
            </div>
          </div>
        </div>
      </section>

      <section id="about" style={sectionStyle}>
        {sectionEyebrow("Profile")}
        <h2 className="section-title">About</h2>

        <div className="premium-card premium-card-large">
          <p style={{ marginTop: 0 }}>
            I am building my portfolio around cloud engineering, data systems, and technical communication with a strong
            emphasis on structured delivery. Rather than presenting isolated tools, I focus on projects that show how
            architecture decisions, deployment workflows, analytics thinking, and documentation come together in a
            practical engineering context.
          </p>

          <p>
            My current direction is centered on AWS, microservices, CI/CD, and portfolio-grade technical work that can
            be understood by both recruiters and engineering teams. I am especially interested in projects that move
            beyond notebooks or coursework and instead demonstrate system design, service separation, infrastructure
            configuration, deployment automation, and business-facing clarity.
          </p>

          <p style={{ marginBottom: 0 }}>
            Long term, I want this portfolio to communicate a clear identity: someone who can work across cloud
            infrastructure, analytics workflows, and implementation detail while still documenting decisions well and
            presenting projects in a professional, credible way.
          </p>
        </div>
      </section>

      <section id="proof-of-value" style={sectionStyle}>
        {sectionEyebrow("Why This Portfolio Matters")}
        <h2 className="section-title">Proof of Value</h2>

        <p className="section-intro">
          This portfolio is designed to show more than coursework. It highlights how I approach delivery,
          architecture decisions, documentation quality, and business-facing communication across cloud and data work.
        </p>

        <div className="grid-three">
          <div className="premium-card premium-card-small hover-card">
            <h3 className="card-heading">Cloud Delivery Thinking</h3>
            <p>
              I focus on projects that show deployment readiness, service separation, infrastructure structure,
              and the ability to explain how cloud systems are designed and released.
            </p>
          </div>

          <div className="premium-card premium-card-small hover-card">
            <h3 className="card-heading">Analytics Communication</h3>
            <p>
              My data work is presented with dashboard thinking, business interpretation, and clearer reporting so
              insights are understandable beyond a technical audience.
            </p>
          </div>

          <div className="premium-card premium-card-small hover-card">
            <h3 className="card-heading">Production-Minded Documentation</h3>
            <p>
              I aim to document projects in a way that is useful to recruiters, engineering teams, and future clients,
              with stronger structure, traceability, and technical credibility.
            </p>
          </div>
        </div>
      </section>

      <section id="what-i-build" style={sectionStyle}>
        {sectionEyebrow("Focus Areas")}
        <h2 className="section-title">What I Build</h2>

        <div className="grid-three">
          <div className="premium-card premium-card-small hover-card">
            <h3 className="card-heading">Cloud Infrastructure</h3>
            <p>
              AWS-based systems involving ECS, ECR, ALB, VPC design, task definitions, deployment assets,
              and CI/CD workflows.
            </p>
            <div>
              <span style={chipStyle}>AWS</span>
              <span style={chipStyle}>ECS</span>
              <span style={chipStyle}>ECR</span>
              <span style={chipStyle}>ALB</span>
              <span style={chipStyle}>CodePipeline</span>
              <span style={chipStyle}>CodeDeploy</span>
            </div>
          </div>

          <div className="premium-card premium-card-small hover-card">
            <h3 className="card-heading">Analytics Systems</h3>
            <p>
              Data science and dashboard work focused on turning datasets into interpretable outputs,
              business-facing insights, and portfolio-grade visual reporting.
            </p>
            <div>
              <span style={chipStyle}>Python</span>
              <span style={chipStyle}>SQL</span>
              <span style={chipStyle}>Pandas</span>
              <span style={chipStyle}>Scikit-learn</span>
              <span style={chipStyle}>Tableau</span>
              <span style={chipStyle}>Power BI</span>
            </div>
          </div>

          <div className="premium-card premium-card-small hover-card">
            <h3 className="card-heading">Application Projects</h3>
            <p>
              Product-oriented work involving React, Flutter, Firebase, documentation, and structured project delivery
              that can be explained clearly to recruiters and teams.
            </p>
            <div>
              <span style={chipStyle}>React</span>
              <span style={chipStyle}>Flutter</span>
              <span style={chipStyle}>Firebase</span>
              <span style={chipStyle}>GitHub</span>
              <span style={chipStyle}>Documentation</span>
            </div>
          </div>
        </div>
      </section>

      <section id="flagship-project" style={sectionStyle}>
        {sectionEyebrow("Flagship Project")}
        <h2 className="section-title section-title-large">AWS Microservices CI/CD Platform</h2>

        <p className="section-intro">
          A portfolio-centrepiece cloud engineering project focused on redesigning a monolithic application into
          containerised microservices with AWS deployment automation and production-style release workflows.
        </p>

        <div className="flagship-metrics">
          <div className="metric-pill">2 microservices</div>
          <div className="metric-pill">AWS ECS + ECR + ALB</div>
          <div className="metric-pill">Blue/green CI/CD</div>
        </div>

        <div className="premium-card flagship-card">
          <div className="flagship-grid">
            <div>
              <img
                src="/assets/aws-architecture-phase1.png"
                alt="AWS microservices architecture diagram"
                style={{ ...flagshipImageStyle, marginBottom: "20px" }}
                onError={(e) => {
                  e.currentTarget.style.display = "none";
                }}
              />

              <img
                src="/assets/aws-vpc-networking.png"
                alt="AWS VPC networking and availability diagram"
                style={flagshipImageStyle}
                onError={(e) => {
                  e.currentTarget.style.display = "none";
                }}
              />
            </div>

            <div>
              <p className="flagship-summary">
                Designed and documented the migration of a monolithic Node.js supplier application into
                containerised microservices on AWS, supported by ECS, ECR, Application Load Balancer routing,
                and blue/green CI/CD deployment.
              </p>

              <h3 className="subheading">Problem</h3>
              <p>
                The original coffee supplier application ran as a monolith and had reliability, scalability,
                and deployment limitations. The objective was to redesign it as microservices so that services
                could scale independently, reduce bottlenecks, and support safer automated releases.
              </p>

              <h3 className="subheading">What I Built</h3>
              <p>
                I worked through a phased cloud engineering implementation that covered architecture planning,
                monolith analysis, service separation, Docker containerisation, image registry management,
                ECS deployment configuration, load balancer routing, CI/CD automation, blue/green deployment,
                and iterative production updates.
              </p>

              <h3 className="subheading">What This Demonstrates</h3>
              <div style={{ marginBottom: "22px" }}>
                <span style={chipStyle}>Microservices Architecture</span>
                <span style={chipStyle}>Docker</span>
                <span style={chipStyle}>Amazon ECS</span>
                <span style={chipStyle}>Amazon ECR</span>
                <span style={chipStyle}>Application Load Balancer</span>
                <span style={chipStyle}>CodePipeline</span>
                <span style={chipStyle}>CodeDeploy</span>
                <span style={chipStyle}>Blue/Green Deployments</span>
                <span style={chipStyle}>System Documentation</span>
              </div>

              <div className="action-row">
                <a
                  href="https://github.com/cam-spec/aws-microservices-lab"
                  target="_blank"
                  rel="noreferrer"
                  style={buttonPrimary}
                >
                  View GitHub Repo
                </a>
                <a href="#case-studies" style={buttonSecondary}>
                  AWS Case Study
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="selected-projects" style={sectionStyle}>
        {sectionEyebrow("Supporting Work")}
        <h2 className="section-title">Selected Projects</h2>

        <div className="grid-two">
          <div className="premium-card premium-card-small hover-card">
            <img
              src="/assets/edueire.jpg"
              alt="EduEire Flutter App logo"
              style={projectImageStyle}
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
            />
            <h3 className="card-heading">EduEire Flutter App</h3>
            <p>
              Built a student-focused Flutter and Firebase application designed to improve access to academic support,
              student discounts, and platform-based feature delivery in a mobile product context.
            </p>
            <a
              href="https://github.com/cam-spec"
              target="_blank"
              rel="noreferrer"
              style={buttonSecondary}
            >
              GitHub Repo
            </a>
          </div>

          <div className="premium-card premium-card-small hover-card">
            <img
              src="/assets/dashboard-project.png"
              alt="Data science dashboard project"
              style={projectImageStyle}
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
            />
            <h3 className="card-heading">Data Science & Dashboard Work</h3>
            <p>
              Built interactive dashboards and machine learning insights to improve data exploration, reporting clarity,
              and business-facing communication across portfolio analytics work.
            </p>
            <div className="action-row">
              <a
                href="https://github.com/cam-spec"
                target="_blank"
                rel="noreferrer"
                style={buttonSecondary}
              >
                GitHub
              </a>
              <a
                href="https://www.linkedin.com/in/camronnjock/"
                target="_blank"
                rel="noreferrer"
                style={buttonSecondary}
              >
                LinkedIn
              </a>
            </div>
          </div>
        </div>
      </section>

      <section id="case-studies" style={sectionStyle}>
        {sectionEyebrow("Deep Dive")}
        <h2 className="section-title">Case Studies</h2>

        <div className="premium-card premium-card-large hover-card">
          <h3 className="card-heading">AWS Microservices CI/CD Platform</h3>
          <p style={{ color: "#cbd5e1" }}>
            Full case study page coming soon. It will cover the business problem, architecture decisions,
            monolith-to-microservices migration, ECS deployment flow, CI/CD pipeline setup, and what the project demonstrates.
          </p>
          <button
            type="button"
            style={{ ...buttonSecondary, cursor: "default" }}
          >
            AWS Case Study — Coming Soon
          </button>
        </div>
      </section>

      <section id="certifications" style={sectionStyle}>
        {sectionEyebrow("Credentials")}
        <h2 className="section-title">Certifications</h2>

        <div className="grid-three">
          <div className="premium-card premium-card-small hover-card">
            <h3 className="card-heading">AWS Academy Graduate - Microservices and CI/CD Pipeline Builder</h3>
            <p style={{ color: "#cbd5e1" }}>
              Hands-on AWS training focused on microservices, deployment workflows, CI/CD concepts,
              and cloud architecture thinking.
            </p>
            <a
              href="https://www.linkedin.com/in/camronnjock/details/certifications/"
              target="_blank"
              rel="noreferrer"
              style={buttonSecondary}
            >
              View Credential
            </a>
          </div>

          <div className="premium-card premium-card-small hover-card">
            <h3 className="card-heading">ChatGPT Prompt Engineering for Developers</h3>
            <p style={{ color: "#cbd5e1" }}>
              Completed the DeepLearning.AI and Coursera project focused on practical prompt engineering,
              AI workflow design, and applying LLMs to real developer tasks.
            </p>
            <a
              href="https://www.coursera.org/projects/chatgpt-prompt-engineering-for-developers-project"
              target="_blank"
              rel="noreferrer"
              style={buttonSecondary}
            >
              View Course
            </a>
          </div>

          <div className="premium-card premium-card-small hover-card">
            <h3 className="card-heading">AWS Cloud Practitioner</h3>
            <p style={{ color: "#cbd5e1" }}>
              Planned addition to strengthen cloud fundamentals, AWS service fluency, and overall platform credibility.
            </p>
            <button
              type="button"
              style={{ ...buttonSecondary, cursor: "default" }}
            >
              Coming Soon
            </button>
          </div>

          <div className="premium-card premium-card-small hover-card">
            <h3 className="card-heading">AWS Solutions Architect Associate</h3>
            <p style={{ color: "#cbd5e1" }}>
              Planned next-step certification to deepen architecture knowledge and strengthen positioning for cloud-focused roles.
            </p>
            <button
              type="button"
              style={{ ...buttonSecondary, cursor: "default" }}
            >
              Coming Soon
            </button>
          </div>
        </div>
      </section>

      <section id="experience" style={sectionStyle}>
        {sectionEyebrow("Background")}
        <h2 className="section-title">Experience</h2>

        <div className="stack-grid">
          <div className="premium-card premium-card-medium hover-card">
            <h3 className="card-heading">IT and Business Intern - ESB</h3>
            <p className="meta-line">2026</p>
            <p style={{ marginBottom: 0 }}>
              Incoming enterprise internship within ESB focused on strengthening exposure to business systems,
              technical delivery, stakeholder communication, and professional standards in a large-scale organisation.
            </p>
          </div>

          <div className="premium-card premium-card-medium hover-card">
            <h3 className="card-heading">Co-Founder - EduEire</h3>
            <p className="meta-line">2025 - Present</p>
            <p style={{ marginBottom: 0 }}>
              Working on a student-focused application concept with emphasis on usability, product thinking,
              feature design, and long-term business potential as the platform develops.
            </p>
          </div>

          <div className="premium-card premium-card-medium hover-card">
            <h3 className="card-heading">Data Analyst Intern - Uptrail</h3>
            <p className="meta-line">2025</p>
            <p style={{ marginBottom: 0 }}>
              Completed a data analyst internship focused on business insight generation, dashboard development,
              data cleaning, and communicating findings clearly. The work involved turning raw data into visual
              reports and recommendations that were easier for non-technical stakeholders to interpret.
            </p>
          </div>

          <div className="premium-card premium-card-medium hover-card">
            <h3 className="card-heading">Passenger Service Agent - Swissport</h3>
            <p className="meta-line">2022 - 2026</p>
            <p style={{ marginBottom: 0 }}>
              Built reliability, teamwork, operational discipline, and communication in a high-volume environment.
            </p>
          </div>
        </div>
      </section>

      <section id="services" style={sectionStyle}>
        {sectionEyebrow("Client-Facing Work")}
        <h2 className="section-title">Services</h2>

        <p className="section-intro">
          These are the areas I am shaping into practical, portfolio-backed offers for cloud deployment support,
          containerisation workflows, and data-driven reporting.
        </p>

        <div className="grid-four">
          <div className="premium-card premium-card-small hover-card">
            <h3 className="card-heading">AWS Deployment Setup</h3>
            <p>
              Support with AWS-ready application deployment, ECS and ECR workflows, task definitions,
              load balancer setup, and cloud-focused architecture for containerised applications.
            </p>
          </div>

          <div className="premium-card premium-card-small hover-card">
            <h3 className="card-heading">Containerisation & CI/CD Preparation</h3>
            <p>
              Dockerising Node.js applications, preparing repositories for deployment, structuring
              microservice-ready codebases, and building cleaner release workflows.
            </p>
          </div>

          <div className="premium-card premium-card-small hover-card">
            <h3 className="card-heading">Interactive Dashboard Development</h3>
            <p>
              Building interactive dashboards using Python, SQL, Streamlit, Tableau, and Power BI to
              create clearer insights, filtering, exploration, and business-facing reporting.
            </p>
          </div>

          <div className="premium-card premium-card-small hover-card">
            <h3 className="card-heading">Work With Me</h3>
            <p>
              I am building client-facing service channels for cloud engineering, deployment support,
              analytics delivery, and portfolio-grade engineering projects.
            </p>

            <div className="action-row">
              <a
                href="https://www.upwork.com/freelancers/~0188c8766407338f32"
                target="_blank"
                rel="noreferrer"
                style={buttonPrimary}
              >
                Upwork
              </a>

              <a
                href="https://www.fiverr.com/camron_njock/buying?source=avatar_menu_profile"
                target="_blank"
                rel="noreferrer"
                style={buttonSecondary}
              >
                Fiverr
              </a>
            </div>
          </div>
        </div>
      </section>

      <section id="contact" style={sectionStyle}>
        {sectionEyebrow("Reach Out")}
        <h2 className="section-title">Contact</h2>

        <div className="premium-card premium-card-large hover-card">
          <p>
            <strong>Email:</strong>{" "}
            <a href="mailto:camronnjock@gmail.com" style={linkStyle}>
              camronnjock@gmail.com
            </a>
          </p>

          <p>
            <strong>LinkedIn:</strong>{" "}
            <a
              href="https://www.linkedin.com/in/camronnjock/"
              target="_blank"
              rel="noreferrer"
              style={linkStyle}
            >
              linkedin.com/in/camronnjock
            </a>
          </p>

          <p>
            <strong>GitHub:</strong>{" "}
            <a
              href="https://github.com/cam-spec"
              target="_blank"
              rel="noreferrer"
              style={linkStyle}
            >
              github.com/cam-spec
            </a>
          </p>

          <div className="action-row" style={{ marginTop: "18px" }}>
            <a href="mailto:camronnjock@gmail.com" style={buttonPrimary}>Email Me</a>
            <a href="/assets/Camron-Njock-CV.pdf" download style={buttonSecondary}>Download CV</a>
            <a
              href="https://www.linkedin.com/in/camronnjock/"
              target="_blank"
              rel="noreferrer"
              style={buttonSecondary}
            >
              LinkedIn
            </a>
            <a
              href="https://github.com/cam-spec"
              target="_blank"
              rel="noreferrer"
              style={buttonSecondary}
            >
              GitHub
            </a>
          </div>
        </div>
      </section>

      <footer className="portfolio-footer">
        © 2026 Camron Njock. All rights reserved.
      </footer>
    </div>
  );
};

export default Portfolio;